/**
 * PD-15585 — ERP-driven payment blocks, shared by the server that enforces
 * them and every payment surface that warns about them before the user
 * presses Pay.
 *
 * Two shapes a customer can build in Workd cannot be represented in some
 * ERPs (HF Foods' Workday today):
 *
 *   1. Spending Account Funds for LESS than the whole available balance. In
 *      Workday the balance is one unapplied customer payment, and a payment
 *      application has to consume the whole payment — apply part of it and
 *      the record sits in Draft, skipped by Auto-Apply.
 *   2. A payment whose total is $0 because applied credit memos cover the
 *      selected invoices. Workday refuses a zero Payment_Amount outright and
 *      has no operation that applies an existing credit memo to an invoice
 *      without a payment.
 *
 * Each block is a tenant setting (`payments.require_full_account_funds_spend`
 * and `payments.block_zero_total_payments`) carrying its own on/off switch
 * and a message per surface. The server resolves the setting into a
 * `PaymentRestrictionsModel` for the caller's surface and ships it with the
 * data the payment screens already load, so every screen shows the same
 * message the server would refuse with.
 *
 * The predicates live here, rather than in each screen, so a screen and the
 * server can never disagree about what counts as "partial" or "zero".
 */

/** One block as the tenant has configured it, resolved for one surface. */
export interface PaymentRestrictionModel {
	/** True when the setting is on, so the condition must be refused. */
	blocked: boolean;
	/**
	 * The tenant's message for the surface that asked (CRM staff or the
	 * portal customer). Empty when the block is off.
	 */
	message: string;
}

export interface PaymentRestrictionsModel {
	/** Spending Account Funds for less than the whole available balance. */
	partialAccountFundsSpend: PaymentRestrictionModel;
	/** A payment whose total is $0 because credit memos cover it. */
	zeroTotalPayment: PaymentRestrictionModel;
}

/** The surface a message is written for. */
export type PaymentSurface = 'crm' | 'portal';

/**
 * What every tenant gets until it opts in: nothing blocked. Also the shape a
 * screen falls back to before its context has loaded, so a block can never
 * flash on while the answer is unknown.
 */
export const NO_PAYMENT_RESTRICTIONS: PaymentRestrictionsModel = {
	partialAccountFundsSpend: { blocked: false, message: '' },
	zeroTotalPayment: { blocked: false, message: '' },
};

/**
 * Cent tolerance for the comparisons below. Amounts on the payment screens
 * are rounded to cents, so anything inside half a cent is "equal".
 */
const CENT_TOLERANCE = 0.005;

/**
 * True when an Account Funds spend of `amount` would leave part of
 * `availableFunds` behind — strictly more than nothing and strictly less
 * than the whole balance. Spending exactly the balance is not partial, and
 * neither is overspending it (that is a different refusal: insufficient
 * funds).
 */
export function isPartialAccountFundsSpend(
	amount: number,
	availableFunds: number,
): boolean {
	return amount > CENT_TOLERANCE && amount < availableFunds - CENT_TOLERANCE;
}

/**
 * True when a payment charges nothing because applied credit covers the
 * invoices: some credit is applied and the net total is zero. A NEGATIVE net
 * (credit exceeding the invoices) is not this case — the screens refuse it
 * separately as over-applied credit.
 */
export function isZeroTotalCreditPayment(
	netTotal: number,
	creditApplied: number,
): boolean {
	return creditApplied > CENT_TOLERANCE && Math.abs(netTotal) <= CENT_TOLERANCE;
}

/**
 * Fills the `{balance}` placeholder a tenant may use in its message with the
 * already-formatted available balance. Other text is returned untouched, so
 * a message without the placeholder still reads correctly.
 */
export function formatPaymentRestrictionMessage(
	message: string,
	formattedBalance: string,
): string {
	return message.replace(/\{balance\}/g, formattedBalance);
}
